// eslint-disable-next-line no-use-before-define
import React, {
  ReactNode, useContext,
} from 'react';

import {
  Box,
  VStack,
  Heading,
  Text,
  Container,
  useColorModeValue,
} from '@chakra-ui/react';
import Head from 'next/head';
import {
  ITerm,
} from '../../lib/graphql';

import BasketFooter from '../../components/basket/basketFooter';
import isMobile from '../../utils/isMobile';
import { NavigationContext } from '../../lib/contexts/NavigationContext';
import getTerms from '../../lib/graphQL/helpers/getTerms';
import Term from './components/Term';
import { getDisplayStatusFromCookies } from '../../lib/utils/previewUtils';

interface ICourseListProps {
  terms: Array<ITerm>
}

// Use a constant for the description
const pageDescription = 'Explore and book dance classes at UK China Performing Arts. View our full schedule for adult and children\'s courses.';

const BookingList = ({
  terms,
}: ICourseListProps): ReactNode => {
  const { isChildSite } = useContext(NavigationContext);
  const titlePrefix = isChildSite ? 'Child' : 'Adult';

  const heroBg = useColorModeValue(
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #2D3748 0%, #4A5568 100%)',
  );
  const heroTextColor = useColorModeValue('white', 'gray.100');

  return (
    <Box minH="100vh">
      <Head>
        <title>{`${titlePrefix} Class Booking`}</title>
        <meta name="description" content={pageDescription} />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>

      {/* Modern Hero Section */}
      <Box
        bg={heroBg}
        py={[12, 16, 20]}
        px={4}
        position="relative"
        overflow="hidden"
      >
        {/* Background Pattern */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.1}
          backgroundImage="radial-gradient(circle at 25% 25%, white 2px, transparent 2px)"
          backgroundSize="50px 50px"
        />

        <Container maxW="1200px" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Heading
              as="h1"
              size={['xl', '2xl', '3xl']}
              color={heroTextColor}
              fontWeight="800"
              letterSpacing="tight"
              lineHeight="1.1"
            >
              {titlePrefix}
              {' '}
              Class Booking
            </Heading>
            <Text
              fontSize={['md', 'lg', 'xl']}
              color="whiteAlpha.900"
              maxW="600px"
              lineHeight="1.6"
            >
              {pageDescription}
            </Text>
            <Box
              w="100px"
              h="4px"
              // bg="whiteAlpha.800"
              borderRadius="full"
              mt={4}
            />
          </VStack>
        </Container>
      </Box>

      {/* Terms Content */}
      <VStack spacing={0} width="100%">
        {terms.map((term) => (
          <Term key={`termlist-item-${term.id}`} term={term} />
        ))}
      </VStack>

      {isMobile() && <BasketFooter />}
    </Box>
  );
};

export const getServerSideProps = async (context) => {
  // Get display status from cookies (new persistent system)
  const displayStatus = getDisplayStatusFromCookies(context.req);

  const { terms } = await getTerms({
    displayStatus,
  });

  return {
    props: {
      terms,
      displayStatus, // Pass to component to maintain state in navigation
    },
  };
};

export default BookingList;
